import React, { useEffect, useCallback, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import {
  fetchContextualTips,
  dismissTip,
  selectActiveTips,
  selectHighPriorityTips,
  selectPersonalizationLoading,
  type ContextualTip,
} from '../../store/slices/dashboardPersonalizationSlice';
import { ContextualTipCard } from './ContextualTipCard';

interface ContextualTipsProviderProps {
  userId: string;
  journeyStage: string;
  skillLevel: string;
  currentScreen?: string;
  maxTips?: number;
  position?: 'top' | 'bottom' | 'inline';
  onTipAction?: (tipId: string, action: string) => void;
}

export function ContextualTipsProvider({
  userId,
  journeyStage,
  skillLevel,
  currentScreen = 'dashboard',
  maxTips = 2,
  position = 'inline',
  onTipAction,
}: ContextualTipsProviderProps) {
  const dispatch = useDispatch<AppDispatch>();

  const activeTips = useSelector(selectActiveTips);
  const highPriorityTips = useSelector(selectHighPriorityTips);
  const loading = useSelector(selectPersonalizationLoading);

  // Fetch tips when component mounts or context changes
  useEffect(() => {
    if (userId && journeyStage && skillLevel) {
      dispatch(fetchContextualTips({ userId, journeyStage, skillLevel }));
    }
  }, [dispatch, userId, journeyStage, skillLevel]);

  // Filter tips based on current screen and conditions
  const relevantTips = useMemo(() => {
    const tips = highPriorityTips.length > 0 ? highPriorityTips : activeTips;

    return tips
      .filter(tip =>
        evaluateTipConditions(tip, {
          currentScreen,
          journeyStage,
          skillLevel,
          userId,
        })
      )
      .slice(0, maxTips);
  }, [
    activeTips,
    highPriorityTips,
    currentScreen,
    journeyStage,
    skillLevel,
    userId,
    maxTips,
  ]);

  // Handle tip dismissal
  const handleDismissTip = useCallback(
    (tipId: string) => {
      dispatch(dismissTip(tipId));
    },
    [dispatch]
  );

  // Handle tip actions
  const handleTipAction = useCallback(
    (tipId: string, action: string) => {
      if (onTipAction) {
        onTipAction(tipId, action);
      }

      // Auto-dismiss tip after action
      if (action !== 'learn_more') {
        handleDismissTip(tipId);
      }
    },
    [onTipAction, handleDismissTip]
  );

  if (loading.tips || relevantTips.length === 0) {
    return null;
  }

  const containerStyle = [
    styles.container,
    position === 'top' && styles.topPosition,
    position === 'bottom' && styles.bottomPosition,
  ];

  return (
    <View style={containerStyle}>
      {relevantTips.map(tip => (
        <ContextualTipCard
          key={tip.id}
          tip={tip}
          onDismiss={() => handleDismissTip(tip.id)}
          onAction={action => handleTipAction(tip.id, action)}
        />
      ))}
    </View>
  );
}

/**
 * Evaluate if a tip should be shown based on its conditions
 */
function evaluateTipConditions(
  tip: ContextualTip,
  context: {
    currentScreen: string;
    journeyStage: string;
    skillLevel: string;
    userId: string;
  }
): boolean {
  // Check if tip has been shown too many times
  if (tip.currentShows >= tip.maxShows) {
    return false;
  }

  // Check if tip was shown recently (within 24 hours for non-critical tips)
  if (tip.lastShown && tip.priority < 0.8) {
    const lastShownTime = new Date(tip.lastShown).getTime();
    const now = new Date().getTime();
    const hoursSinceLastShown = (now - lastShownTime) / (1000 * 60 * 60);

    if (hoursSinceLastShown < 24) {
      return false;
    }
  }

  // Evaluate all conditions
  return tip.conditions.every(condition => {
    switch (condition.type) {
      case 'journey_stage':
        return evaluateCondition(
          context.journeyStage,
          condition.operator,
          condition.value
        );

      case 'skill_level':
        return evaluateCondition(
          context.skillLevel,
          condition.operator,
          condition.value
        );

      case 'time_based':
        const now = new Date();
        const currentHour = now.getHours();
        return evaluateCondition(
          currentHour,
          condition.operator,
          condition.value
        );

      case 'feature_usage':
        // This would require additional context about feature usage
        // For now, return true as a placeholder
        return true;

      default:
        return true;
    }
  });
}

/**
 * Evaluate a single condition
 */
function evaluateCondition(
  actualValue: any,
  operator: string,
  expectedValue: any
): boolean {
  switch (operator) {
    case 'equals':
      return actualValue === expectedValue;

    case 'greater_than':
      return actualValue > expectedValue;

    case 'less_than':
      return actualValue < expectedValue;

    case 'contains':
      if (Array.isArray(actualValue)) {
        return actualValue.includes(expectedValue);
      }
      return String(actualValue).includes(String(expectedValue));

    case 'not_contains':
      if (Array.isArray(actualValue)) {
        return !actualValue.includes(expectedValue);
      }
      return !String(actualValue).includes(String(expectedValue));

    default:
      return false;
  }
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  topPosition: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
  bottomPosition: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
});
